import { useMutation, useQueryClient, type UseMutateFunction } from '@tanstack/react-query';
import { companyUserService } from '../services/company-user.service';
import { useAuthStore } from '@/features/auth/stores/authStore';
import type { CompanyUser } from '../types/company-admin.types';

export function useCompanyUserStatus(): {
  deactivate: UseMutateFunction<CompanyUser, Error, string>;
  isDeactivating: boolean;
  activate: UseMutateFunction<CompanyUser, Error, string>;
  isActivating: boolean;
  sendPasswordReset: UseMutateFunction<void, Error, string>;
  isSendingReset: boolean;
} {
  const queryClient = useQueryClient();
  const getEffectiveCompanyId = useAuthStore((state) => state.getEffectiveCompanyId);

  const invalidateUsers = (): void => {
    const companyId = getEffectiveCompanyId();
    queryClient.invalidateQueries({ queryKey: ['company-users', companyId] });
  };

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => companyUserService.deactivateUser(userId),
    onSuccess: invalidateUsers,
  });

  const activateMutation = useMutation({
    mutationFn: (userId: string) => companyUserService.activateUser(userId),
    onSuccess: invalidateUsers,
  });

  const sendPasswordResetMutation = useMutation({
    mutationFn: (userId: string) => companyUserService.sendPasswordReset(userId),
  });

  return {
    deactivate: deactivateMutation.mutate,
    isDeactivating: deactivateMutation.isPending,
    activate: activateMutation.mutate,
    isActivating: activateMutation.isPending,
    sendPasswordReset: sendPasswordResetMutation.mutate,
    isSendingReset: sendPasswordResetMutation.isPending,
  };
}
